/**
 * 视频调性档 toneRegister(2026-09-24,视频仓 0924 车 C,契约 §3.1)。
 *
 * 三档:`hardsell` 带货喊话 / `restrained` 品牌克制 / `luxury` 高奢静默。幕一目的流「时长/比例」
 * 卡里的三颗 chip 选它;提交时随 `toneRegister` 发给 api(api 只校验枚举 + 透传),同一个值也写进
 * 草稿 formJson 让导演在 compose 阶段就知道档位,拍法卡 taxonomy 请求带 `?register=`。
 *
 * ★行业码 → 档位表与 ai `app/modules/video/domain/tone_register.py` 的 `resolve_tone_register`
 *   **同源**(ai 是单一真源,这里是镜像,只用来算 chip 的默认值)。改表先改 ai,再同步这里 ——
 *   两边不一致时以 ai 为准(它是真正拿去编排的那一端)。行业码全集取自 api
 *   `db/migration/V1__init.sql` 的 `xb_industry` INSERT(26 个)+ 珠宝行业 `IND_JEWELRY`(V3,0922 新增)。
 *   奢侈品/腕表/高定/艺术、房产/汽车/品牌服务在库里还没有行业码,两边按 ai 定稿的码名预埋。
 *   匹配口径同 ai `register_for_industry`:去空白 + 转大写后**精确**匹配,不做模糊。
 *
 * ★默认值规则:用户**没点过** chip → 跟租户行业推;点过 → 用户的选择为准,不再跟行业
 *   (`toneRegisterTouched`)。行业未知 → hardsell(与 ai 缺省一致)。
 */
export type ToneRegister = "hardsell" | "restrained" | "luxury";

export const TONE_REGISTERS: readonly ToneRegister[] = ["hardsell", "restrained", "luxury"];

/** chip 文案(顺序 = 渲染顺序)。 */
export const TONE_REGISTER_OPTIONS: readonly {
  value: ToneRegister;
  label: string;
  hint: string;
}[] = [
  { value: "hardsell", label: "带货喊话", hint: "快切、强钩子、画面大字,促单直给" },
  { value: "restrained", label: "品牌克制", hint: "节奏稳、弱促销,大字克制" },
  { value: "luxury", label: "高奢静默", hint: "慢镜留白、沉稳配音,基本不压大字" },
];

/**
 * 高奢静默:珠宝 / 奢侈品 / 腕表 / 高定 / 艺术。
 * ★与 ai `LUXURY_INDUSTRY_CODES` 逐字一致(0924 C-ai 定稿);IND_JEWELRY 之外的四个码库里暂无,预埋。
 */
const LUXURY_INDUSTRIES: readonly string[] = [
  "IND_JEWELRY",
  "IND_LUXURY",
  "IND_WATCH",
  "IND_HAUTE_COUTURE",
  "IND_ART",
];

/**
 * 品牌克制:金融 / 医疗 / 法律 / 教育 / 房产 / 汽车 / 品牌服务。
 * ★与 ai `RESTRAINED_INDUSTRY_CODES` 逐字一致;IND_REAL_ESTATE / IND_AUTOMOTIVE / IND_BRAND_SERVICE 库里暂无,预埋。
 */
const RESTRAINED_INDUSTRIES: readonly string[] = [
  // 金融
  "IND_FINANCE",
  "IND_FINANCE_PRO",
  // 医疗
  "IND_HEALTHCARE",
  "IND_MED_DEVICE",
  "IND_PROSTHETICS",
  "IND_MED_INSTITUTION",
  "IND_PHARMA",
  // 法律 / 财税 / 知识产权(商标品牌)
  "IND_PRO_SERVICE",
  "IND_IP_SERVICE",
  // 教育
  "IND_EDUCATION",
  "IND_K12_TRAINING",
  "IND_VOC_EDU",
  // 房产 / 汽车 / 品牌服务
  "IND_REAL_ESTATE",
  "IND_AUTOMOTIVE",
  "IND_BRAND_SERVICE",
];

/** 行业码 → 档位的完整表(未列出的行业码 = hardsell)。导出给单测逐条对账。 */
export const INDUSTRY_TONE_REGISTER: Readonly<Record<string, ToneRegister>> = Object.freeze({
  ...Object.fromEntries(LUXURY_INDUSTRIES.map((c) => [c, "luxury" as const])),
  ...Object.fromEntries(RESTRAINED_INDUSTRIES.map((c) => [c, "restrained" as const])),
});

export function isToneRegister(v: unknown): v is ToneRegister {
  return typeof v === "string" && (TONE_REGISTERS as readonly string[]).includes(v);
}

/** 按行业码推默认档;未知/空 → hardsell。 */
export function defaultToneRegisterFor(industryCode: string | null | undefined): ToneRegister {
  if (!industryCode) return "hardsell";
  return INDUSTRY_TONE_REGISTER[industryCode.trim().toUpperCase()] ?? "hardsell";
}

/** chip 当前显示/发送的档:用户点过用用户的,否则跟行业推。 */
export function effectiveToneRegister(
  form: { toneRegister?: ToneRegister | null; toneRegisterTouched?: boolean },
  industryCode: string | null | undefined
): ToneRegister {
  if (form.toneRegisterTouched && isToneRegister(form.toneRegister)) return form.toneRegister;
  return defaultToneRegisterFor(industryCode);
}

export function toneRegisterLabel(v: unknown): string | null {
  return TONE_REGISTER_OPTIONS.find((o) => o.value === v)?.label ?? null;
}

/**
 * 导演判的画面大字档(0924 车 C,契约 §3.2)——只读展示,console 不改写。
 * 渲染期载体是 `hookPlan.textOverlay`;编排响应顶层另回一份 `textOverlay`(被调性档封顶时带 `cappedFrom`)。
 */
export type TextOverlayMode = "none" | "minimal" | "full";
export interface TextOverlayInfo {
  mode: TextOverlayMode;
  reason?: string;
  cappedFrom?: TextOverlayMode;
}

const OVERLAY_LABEL: Record<TextOverlayMode, string> = {
  none: "不压大字",
  minimal: "仅品牌与落版",
  full: "满屏大字",
};

function asOverlay(v: unknown): TextOverlayInfo | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  if (o.mode !== "none" && o.mode !== "minimal" && o.mode !== "full") return null;
  const capped = o.cappedFrom;
  return {
    mode: o.mode,
    ...(typeof o.reason === "string" && o.reason.trim() ? { reason: o.reason.trim() } : {}),
    ...(capped === "none" || capped === "minimal" || capped === "full"
      ? { cappedFrom: capped }
      : {}),
  };
}

/** 取大字档:优先渲染载体 hookPlan.textOverlay,其次编排响应顶层 textOverlay;都没有 → null(老单不显示)。 */
export function textOverlayOf(hookPlan: unknown, topLevel?: unknown): TextOverlayInfo | null {
  const fromHook =
    hookPlan && typeof hookPlan === "object"
      ? asOverlay((hookPlan as Record<string, unknown>).textOverlay)
      : null;
  return fromHook ?? asOverlay(topLevel);
}

/** 「大字:仅品牌与落版(minimal)」这类展示串;被封顶时补一句从哪档收下来的。 */
export function textOverlayText(info: TextOverlayInfo | null): string | null {
  if (!info) return null;
  const base = `${OVERLAY_LABEL[info.mode]}(${info.mode})`;
  return info.cappedFrom && info.cappedFrom !== info.mode
    ? `${base} · 按调性从 ${info.cappedFrom} 收紧`
    : base;
}
